<template>
    <div class="container-fluid d-flex justify-content-center">
        <div style="max-width: 800px; width: 100%; text-align:center; margin-top:20px;">
            <Flash v-if="flashMessage" alert="danger">{{ flashMessage }}</Flash>
        </div>
    </div>
    <BoxLayout>
        <form v-on:submit.prevent="register" v-if="isValid && !isSuccess">
            <div class="mb-2">
                <TextInput
                    label="First name"
                    v-model="v$.first_name.$model"
                    :v="v$.first_name"
                />
            </div>
            <div class="mb-2">
                <TextInput
                    label="Last name"
                    v-model="v$.last_name.$model"
                    :v="v$.last_name"
                />
            </div>
            <div class="mb-2">
                <TextInput
                label="Password"
                v-model="v$.password.$model"
                :v="v$.password"
                type="password"
                @on-click="showPasswordHelper = true"
                />
            </div>
            <div class="mb-2">
                <PasswordModal
                v-show="showPasswordHelper"
                :password="v$.password.$model"
                />
            </div>
            <div class="mb-2">
                <TextInput
                label="Confirm password"
                v-model="v$.password_confirm.$model"
                :v="v$.password_confirm"
                type="password"
                />
            </div>
            <div class="mb-2">
                <DropdownInput
                    prependIcon="bi-briefcase"
                    label="Your Role"
                    v-model="v$.connect_user_role_id.$model"
                    :v="v$.connect_user_role_id"
                    :options="roles"
                    disable-first-option="true"
                />
            </div>
    <transition name="fade">
        <div v-if="true == showDentistField" class="details-wrapper">
            <div class="mb-2">
                <DropdownInput
                    prependIcon="bi-person"
                    label="Your Details"
                    v-model="v$.dentist_id.$model"
                    :v="v$.dentist_id"
                    :options="practiceDentists"
                />
            </div>
            <div class="mb-2" v-if="dentist_id && showEmailField">
                <TextInput
                    prependIcon="bi-envelope"
                    label="Email address"
                    v-model="v$.email.$model"
                    :v="v$.email"
                    :class="{ 'is-invalid': v$.email.$error }"
                />
            </div>
            <div class="mb-2" v-if="dentist_id && showPhoneField">
                <PhoneInput
                    v-model:phone-number="phone"
                    v-model:country-code="currentPhoneCode"
                    :class="{ 'is-invalid': v$.phone.$error }"
                    label="Mobile Number"
                    placeholder="07234 123 456"
                    @blur="v$.phone.$touch"
                    ref="phoneInput"
                />
                <div v-if="v$.phone.$error" class="invalid-feedback d-block">
                    Please enter a valid phone number
                </div>
            </div>
            <div class="mb-2" v-if="dentist_id && showAllowPromoField">
                <TextInput
                    prependIcon="bi-bell-fill"
                    label="Allow promotional content"
                    v-model="v$.allow_promotional_content.$model"
                    :v="v$.allow_promotional_content"
                />
            </div>
        </div>
    </transition>
            <br/>
            <button type="submit" class="btn w-100 btn-primary">Register</button>
        </form>
        <div v-if="!isValid" class="text-center">
            <p>This invitation is no longer valid.</p>
            <p>Please request a new invitation or <router-link to="/login">sign in</router-link> if you have successfully registered with this invitation previously.</p>
        </div>
        <div v-if="isSuccess" class="text-center">
            <p>You have successfully registered for Swift Connect.</p>
            <p>Please <router-link to="/login">sign in</router-link> using the details you provided. </p>
        </div>
    </BoxLayout>
</template>

<script>
    import { ref } from 'vue';
    import api from "@/services/api";
    import BoxLayout from '@/components/BoxLayout.vue';
    import TextInput from '@/components/TextInput.vue';
    import DropdownInput from '@/components/DropdownInput.vue';
    import PasswordModal from '@/components/PasswordModal.vue';
    import PhoneInput from '@/components/PhoneInput.vue';
    import Flash from "@/components/Flash.vue";
    
    import useVuelidate from '@vuelidate/core'
    import { required, sameAs, email, helpers } from '@vuelidate/validators'

    const ROLE_PRINCIPAL_DENTIST = 1;
    const ROLE_ASSOCIATE_DENTIST = 2;

    export default {
        props: {
            code: {
                required: true
            }
        },
        data() {
            return {
                first_name: '',
                last_name: '',
                password: '',
                password_confirm: '',
                alertMessage: null,
                isSuccess: false,
                isValid: true,
                showPasswordHelper: false,
                practiceDentists: [],
                roles: [],
                dentist_id: null,
                connect_user_role_id: null,
                selectedDentist: null,
                email: '',
                phone: '',
                currentPhoneCode: '',
                showEmailField: false,
                showPhoneField: false,
                showDentistField: false,
                flashMessage: '',
            }
        },
        setup () {
            const externalResults = ref({});
            return {
                externalResults,
                v$: useVuelidate({ $externalResults: externalResults }),
            }
        },
        components: {
            BoxLayout,
            TextInput,
            DropdownInput,
            PasswordModal,
            PhoneInput,
            Flash
        },
        validations () {
            let validations = {};
            validations = {
                first_name: { required },
                last_name: { required },
                password: { required },
                connect_user_role_id: { required },
                password_confirm: { required, sameAs: sameAs(this.password) },
            }

            if (this.connect_user_role_id == ROLE_PRINCIPAL_DENTIST || this.connect_user_role_id == ROLE_ASSOCIATE_DENTIST) {
                const phoneRegex = helpers.regex(/^[\d\s]+$/);
                const validatePhoneLength = helpers.withMessage(
                    'Phone number must be 10 digits, or 11 digits if starting with 0',
                    (value) => {
                        const digitsOnly = value.replace(/\s/g, '');
                        if (digitsOnly.startsWith('0')) {
                            return digitsOnly.length === 11;
                        } else {
                            return digitsOnly.length === 10;
                        }
                    }
                );
                
                validations.dentist_id = { required };
                validations.email = { email, required };
                validations.phone = { required, phoneRegex, validatePhoneLength };
            }

            return validations;
        },
        methods: {
            register: async function () {
                var isValid = await this.v$.$validate();

                if(isValid)
                {
                    this.requestRegister();
                }
                else
                {
                    console.log('Validation errors', this.v$.$errors.map((error) => { return error.$property + ': ' + error.$message}));
                }
            },
            requestRegister: function () {

                // Get formatted phone from PhoneInput component
                const formattedPhone = this.$refs.phoneInput ? 
                    this.$refs.phoneInput.getFormattedPhoneForSubmission() : 
                    this.phone;
                

               const payload = {
                    first_name: this.first_name,
                    last_name: this.last_name,
                    password: this.password,
                    invitation: this.code,
                    connect_user_role_id: this.connect_user_role_id,
                };

                const role = this.roles.find((role) => role.id == this.connect_user_role_id);

                if (role.is_dentist) {
                    payload.dentist_id = this.dentist_id;
                    payload.email = this.email;
                    payload.phone = formattedPhone;
                    payload.country_code = `+${this.currentPhoneCode}`;
                }





                api.post('/api/v1/register', payload)
                .then(() => {
                    this.isSuccess = true;
                })
                .catch((error) => {
                    if(error.response)
                    {
                        if([400,401,403,404,422,500].includes(error.response.status)) {
                            this.flashMessage = 'There was a problem signing in. Please try again later.';
                        }
                        Object.assign(this.externalResults, error.response.data?.errors)
                    }
                    else
                    {
                        this.flashMessage = 'There was a problem signing in. Please try again later.';
                    }
                    this.scrollToTop();
                });
            },
            checkInvitation: function () {
                api
                .get('/api/v1/invitation/' +  this.code)
                .then(() => {
                    // Responded OK
                    console.log('Invitation valid');
                    this.fetchDentists();
                })
                .catch(() => {
                    console.log('Invitation invalid');
                    this.isValid = false;
                });
            },
            fetchDentists: async function() {
                try {
                    const res = await api.get('/api/v1/invitation/dentists/' + this.code);
                    await this.fetchRoles();
                    this.practiceDentists = res.data.data.map((dentist) => {
                        return {
                            id: dentist.id,
                            label: `${dentist.title} ${dentist.first_name} ${dentist.surname}`,
                            email: dentist.email,
                            phone: dentist.phone,
                        }
                    })
                } catch (e) {
                    console.log('Failed to fetch dentists:', e);
                }
            },
            async fetchRoles() {
                try {
                    const response = await api.get('/api/v1/roles');
                    const roleData = response.data.roles;
                    this.roles = roleData;
                } catch (error) {
                    console.error('Failed to get all roles', error);
                }
            },
            scrollToTop() {
                window.scrollTo({ 
                    top: 0, 
                    behavior: 'smooth' 
                });
            },
        },
        mounted () {
            if(this.$store.state.isSignedIn)
            {
                this.$store.dispatch('signOut')
                .then(() => {
                    this.checkInvitation();
                });
            }
            else
            {
                this.checkInvitation();
            }
        },
         watch: {
            connect_user_role_id(newRoleId) {
                this.showDentistField = (newRoleId == ROLE_ASSOCIATE_DENTIST || newRoleId == ROLE_PRINCIPAL_DENTIST);
                if (!this.showDentistField) {
                    this.dentist_id = null;
                    this.selectedDentist = null;
                    this.email = '';
                    this.phone = '';
                    this.currentPhoneCode = '';
                    this.showEmailField = false;
                    this.showPhoneField = false;
                }
            },
            dentist_id(newDentistId) {
                const selectedDentist = this.practiceDentists.find((dentist) => dentist.id == newDentistId) || null;
                this.selectedDentist = selectedDentist;
                
                this.showEmailField = !selectedDentist?.email;
                this.showPhoneField = !selectedDentist?.phone;
                
                this.email = selectedDentist?.email || '';
                
                // Handle phone number parsing using PhoneInput component
                if (selectedDentist?.phone) {
                    this.$nextTick(() => {
                        if (this.$refs.phoneInput) {
                            const phoneData = this.$refs.phoneInput.parseInternationalPhone(selectedDentist.phone);
                            this.phone = phoneData.localNumber;
                            this.currentPhoneCode = phoneData.countryCode || '';
                        } else {
                            // Fallback if component not ready
                            this.phone = selectedDentist.phone;
                            this.currentPhoneCode = '';
                        }
                    });
                } else {
                    this.phone = '';
                }
            },
        },
    }
</script>